import type { ApiResponse } from '@en3/shared';
import { ApiError, NETWORK_ERROR_MESSAGE } from './errors';
import { getAccessToken } from './token';

/**
 * API 客户端。
 *
 * 职责：
 * 1. 拼路径、带凭据（Cookie）、自动附加 `Authorization: Bearer <accessToken>`；
 * 2. 解统一信封 `{ok, data}` / `{ok:false, error, requestId}`，把失败转成 `ApiError`；
 * 3. **401 自动刷新并重放一次**（单飞：并发 401 只会触发一次刷新，避免刷新令牌被多次轮换而互相失效）。
 *
 * 不负责：业务语义、缓存（交给 TanStack Query）、UI 提示。
 */

/**
 * 同源部署时留空即可（走 Vite 代理 / Pages 路由）。
 * 这里用可选链读取：浏览器里由 Vite 注入 `import.meta.env`，
 * 而在 Node 下直接导入本模块做逻辑测试时 `import.meta.env` 不存在，也不会抛错。
 */
const BASE = (
  (import.meta.env as unknown as Record<string, string | undefined> | undefined)?.VITE_API_BASE ?? ''
).replace(/\/$/, '');

/** 这些端点自身就参与登录态，不参与"401 自动刷新"以免递归。 */
const AUTH_ENDPOINTS = ['/api/auth/refresh', '/api/auth/login', '/api/auth/register', '/api/auth/logout'];

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** 为 true 时不自动刷新（内部使用，也用于"静默探测"场景） */
  skipRefreshRetry?: boolean;
}

async function rawRequest(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  try {
    return await fetch(`${BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      // 刷新令牌在 HttpOnly Cookie 里，必须显式带凭据（同源默认也会带，这里写明意图）
      credentials: 'include',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError({ code: 'NETWORK_ERROR', status: 0, message: NETWORK_ERROR_MESSAGE });
  }
}

async function parseEnvelope<T>(res: Response): Promise<T> {
  let payload: ApiResponse<T> | null = null;
  const text = await res.text();

  if (text.length > 0) {
    try {
      payload = JSON.parse(text) as ApiResponse<T>;
    } catch {
      // 非 JSON（例如反代返回的 HTML 错误页）——统一按服务端异常处理
      throw new ApiError({
        code: 'INTERNAL',
        status: res.status,
        message: res.status === 404 ? '接口不存在（请检查部署与路由配置）' : undefined,
      });
    }
  }

  if (!res.ok || payload === null || payload.ok === false) {
    const failure = payload && payload.ok === false ? payload : null;
    throw new ApiError({
      code: failure?.error.code ?? 'INTERNAL',
      status: res.status,
      ...(failure?.error.message ? { message: failure.error.message } : {}),
      ...(failure?.error.details === undefined ? {} : { details: failure.error.details }),
      ...(failure?.requestId ? { requestId: failure.requestId } : {}),
    });
  }

  return payload.data;
}

/**
 * 单飞刷新：并发 401 只触发一次 /api/auth/refresh。
 */
let refreshInFlight: Promise<boolean> | null = null;

export type RefreshHandler = () => Promise<boolean>;

let refreshHandler: RefreshHandler | null = null;

/**
 * 由 authStore 注入"如何刷新"。这样 client 不需要知道用户模型，
 * 也避免 client ↔ store 的循环依赖。
 */
export function setRefreshHandler(handler: RefreshHandler | null): void {
  refreshHandler = handler;
}

export type AuthReadyWaiter = () => Promise<void>;

let authReadyWaiter: AuthReadyWaiter | null = null;

/**
 * 由 authStore 注入"登录态何时确定"。
 *
 * 为什么需要它（**这是 E2E 真实跑出来的三个缺陷的共同根因**）：
 * 访问令牌只存在内存里，硬刷新或从书签打开页面时，`authStore.bootstrap()`
 * 正拿着 HttpOnly 刷新 Cookie 去换新令牌；而页面上的数据请求往往同时发出。
 * 那一刻服务端按"未登录"处理，于是：
 * - 用户自己的**私有文章 / 私有教材**在列表里根本不出现；
 * - 私有文章的正文接口返回的是 **404（当作不存在，防探测）而不是 401**，
 *   所以"401 自动刷新后重放"这条兜底压根不会触发 —— 用户反复刷新也看不到自己的文章；
 * - 依赖登录上下文的跨文章列表（长难句）同样会少掉自己的内容。
 *
 * 与其在每个页面各写一遍 `enabled: status !== 'loading'`（漏一个就复发），
 * 不如在这里统一等一次：**所有非认证类请求都先等登录态确定**。
 * 代价是首屏多一次等待（没有 Cookie 时 refresh 很快就返回 401）。
 */
export function setAuthReadyWaiter(waiter: AuthReadyWaiter | null): void {
  authReadyWaiter = waiter;
}

/** 等待登录态确定；超时（5 秒）后照常发请求，避免 bootstrap 卡住时整站不可用。 */
const AUTH_READY_TIMEOUT_MS = 5000;

async function waitForAuth(): Promise<void> {
  if (!authReadyWaiter) return;
  try {
    await Promise.race([
      authReadyWaiter(),
      new Promise<void>((resolve) => setTimeout(resolve, AUTH_READY_TIMEOUT_MS)),
    ]);
  } catch {
    // 等不到就照常发请求：宁可让个别请求 401/404，也不要整页卡住
  }
}

export async function tryRefreshOnce(): Promise<boolean> {
  if (!refreshHandler) return false;
  if (!refreshInFlight) {
    refreshInFlight = refreshHandler().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  // 认证类端点自己要参与登录态判定，等它就会死锁（bootstrap → refresh）
  if (!AUTH_ENDPOINTS.includes(path)) await waitForAuth();

  const res = await rawRequest(path, options);

  if (res.status === 401 && !options.skipRefreshRetry && !AUTH_ENDPOINTS.includes(path)) {
    const refreshed = await tryRefreshOnce();
    if (refreshed) {
      return parseEnvelope<T>(await rawRequest(path, { ...options, skipRefreshRetry: true }));
    }
  }

  return parseEnvelope<T>(res);
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', ...(body === undefined ? {} : { body }) }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', ...(body === undefined ? {} : { body }) }),
  /**
   * DELETE 允许带 body。
   *
   * 这是刻意的（虽然不太 RESTful）：注销账号这类操作必须带上
   * `{ confirm: true, mode }` 这样的确认载荷，把它塞进 query 反而更容易被日志/埋点记录下来。
   * 后端对应的接口用 `parseJsonBody` 读取，语义与 POST 一致。
   */
  delete: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'DELETE', ...(body === undefined ? {} : { body }) }),
};

/** 查询键集中管理，避免各处手写字符串导致缓存失效不干净。 */
export const queryKeys = {
  me: ['me'] as const,
  recent: ['me', 'recent'] as const,
  health: ['health'] as const,
  textbooks: (params?: Record<string, unknown>) => ['textbooks', params ?? {}] as const,
  textbook: (id: string) => ['textbooks', id] as const,
  articles: (params?: Record<string, unknown>) => ['articles', params ?? {}] as const,
  article: (id: string) => ['articles', 'detail', id] as const,
  articleLongSentences: (id: string) => ['articles', 'long-sentences', id] as const,
  /**
   * 查词键刻意包含上下文。
   *
   * 同一个词在不同句子里的「文中义」可能不同，只用 lemma 做键会让用户
   * 在第二句点同一个词时看到上一句的文中义 —— 这是很容易被忽略的错。
   * 上下文只取前 80 字符参与键，避免键过长。
   */
  lookup: (lemma: string, context?: string) => ['lookup', lemma, context?.slice(0, 80) ?? ''] as const,
  wordbook: (params?: Record<string, unknown>) => ['wordbook', params ?? {}] as const,
  reviewQueue: (params?: Record<string, unknown>) => ['review', 'queue', params ?? {}] as const,
  longSentences: (params?: Record<string, unknown>) => ['long-sentences', params ?? {}] as const,
  analysis: (key: string) => ['analysis', key] as const,
  advice: (articleId: string, mode?: string) => ['advice', articleId, mode ?? 'default'] as const,
  levelReport: ['level', 'report'] as const,
  adminOverview: ['admin', 'overview'] as const,
  adminAiCache: (params?: Record<string, unknown>) => ['admin', 'ai-cache', params ?? {}] as const,
  adminUsers: (params?: Record<string, unknown>) => ['admin', 'users', params ?? {}] as const,
};

/** 写生词本后需要失效的键：生词本是多个页面的数据源。 */
export const WORDBOOK_RELATED_KEYS = [
  ['wordbook'],
  ['me'],
  ['review'],
] as const;
